import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { DatePicker } from '../ui/DatePicker';
import { EventTypeSelect } from '../calendar/EventTypeSelect';
import { useEventTypes } from '../../hooks/queries/useEventTypes';
import { Textarea } from '../ui/textarea';
import { useCollaborators } from '../../hooks/queries/useCollaborators';
import { useDossierContacts } from '../../hooks/queries/useContacts';
import {
  DOSSIER_CONTACT_ROLE_LABELS,
  getContactDisplayName,
} from '../../services/contacts/contactService';
import {
  defaultEndTime,
  setHearingCourt,
  setHearingEnd,
  setHearingStart,
  setHearingType,
  toggleHearingPerson,
  type HearingDraft,
  type HearingPerson,
} from '../../services/dossiers/hearingForm';

const CABINET_ROLES = new Set(['admin', 'lawyer', 'secretary']);

const LBL = 'mb-1.5 block text-[11px] font-bold uppercase tracking-[0.12em] text-cime-text-muted';
const CHIP_KICK = 'block text-[10px] font-bold uppercase tracking-[0.12em] text-[#8a928c]';
const CHIP = 'block min-w-0 rounded-xl border border-[#e6e2d8] bg-white px-3 py-2 text-left';
const VALUE = 'mt-0.5 block truncate text-sm font-bold';
const FIELD = 'mt-0 min-h-[92px] resize-none rounded-xl border-[#e6e2d8] px-3 py-2.5 focus-visible:ring-cime-primary/30 focus-visible:ring-offset-0';

function longDay(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  return format(new Date(y, m - 1, d), 'EEEE d MMMM yyyy', { locale: fr });
}

export function useHearingPeopleOptions(
  dossierId: string | undefined,
  keepCollaboratorIds: string[],
) {
  const { data: collaborators = [] } = useCollaborators();
  const { data: linked = [] } = useDossierContacts(dossierId);
  const keepKey = [...keepCollaboratorIds].filter(Boolean).sort().join(',');

  return useMemo(() => {
    const keep = new Set(keepKey ? keepKey.split(',') : []);
    const staff = collaborators.filter(
      (person) => (person.is_active && CABINET_ROLES.has(person.role)) || keep.has(person.id),
    );
    const parties = new Map<string, { id: string; name: string; role: string }>();
    for (const row of linked) {
      const role = DOSSIER_CONTACT_ROLE_LABELS[row.role] ?? row.role;
      const existing = parties.get(row.contact_id);
      if (existing) {
        if (!existing.role.split(', ').includes(role)) existing.role = `${existing.role}, ${role}`;
        continue;
      }
      parties.set(row.contact_id, {
        id: row.contact_id,
        name: getContactDisplayName(row.contact),
        role,
      });
    }
    return {
      collaborators: staff.map((person) => ({ id: person.id, name: person.full_name })),
      parties: [...parties.values()],
    };
  }, [collaborators, keepKey, linked]);
}

function personChecked(people: HearingPerson[], person: HearingPerson): boolean {
  return people.some((item) => item.kind === person.kind && item.id === person.id);
}

export function HearingFields({
  draft,
  onChange,
  disabled,
  collaborators,
  parties,
  title,
  context,
  onChangeContext,
}: {
  draft: HearingDraft;
  onChange: (next: HearingDraft) => void;
  disabled?: boolean;
  collaborators: { id: string; name: string }[];
  parties: { id: string; name: string; role: string }[];
  title: ReactNode;
  context?: string;
  onChangeContext?: () => void;
}) {
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const { data: eventTypes = [] } = useEventTypes();
  const hearingTypeNames = eventTypes.filter((t) => t.family === 'hearing').map((t) => t.name);

  function commit(next: HearingDraft) {
    draftRef.current = next;
    onChange(next);
  }

  const endLocked = !draft.day || defaultEndTime(draft.startTime) === '';

  return (
    <div>
      <header className="flex gap-[18px] border-b border-[#e6e2d8] bg-[linear-gradient(135deg,rgba(4,52,41,0.06),transparent_42%),#fff] px-6 pb-[18px] pt-[22px]">
        <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-cime-primary text-[#c9a84c]" aria-hidden="true">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path d="M4 20V9.5L12 4l8 5.5V20" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
            <path d="M9 20v-6h6v6" stroke="currentColor" strokeWidth="1.6" />
          </svg>
        </div>
        <div className="min-w-0 flex-1 pr-8">
          {title}
          <input
            value={draft.label}
            onChange={(event) => commit({ ...draftRef.current, label: event.target.value })}
            placeholder="Libellé de l’audience"
            disabled={disabled}
            aria-label="Libellé"
            className="mt-0.5 w-full border-0 bg-transparent p-0 text-[28px] leading-[1.15] text-cime-text-primary placeholder:text-neutral-300 focus:outline-none disabled:opacity-60"
            style={{ fontFamily: 'var(--cime-font-heading)' }}
          />
          {context ? (
            <p className="mt-1.5 flex min-w-0 items-baseline gap-3 text-[13px] text-cime-text-muted">
              <span className="truncate">{context}</span>
              {onChangeContext ? (
                <button
                  type="button"
                  onClick={onChangeContext}
                  className="shrink-0 font-bold text-cime-primary hover:underline"
                >
                  Changer
                </button>
              ) : null}
            </p>
          ) : null}
          <EventTypeSelect
            family="hearing"
            value={draft.eventTypeId}
            onChange={(_, type) => commit(setHearingType(draftRef.current, type, hearingTypeNames))}
            disabled={disabled}
            className="mt-3"
          />
        </div>
      </header>

      <div className="grid grid-cols-1 gap-2.5 border-b border-[#e6e2d8] bg-[#f6f4ee] px-6 py-3.5 md:grid-cols-[1.1fr_0.9fr_1.4fr]">
        <DatePicker
          value={draft.day}
          onChange={(day) => commit(setHearingStart(draftRef.current, day, draftRef.current.startTime))}
          disabled={disabled}
          aria-label="Jour de l’audience"
          trigger={(
            <span className={CHIP}>
              <span className={CHIP_KICK}>Jour</span>
              <strong className={draft.day ? VALUE : `${VALUE} text-[#8a928c]`}>
                {draft.day ? longDay(draft.day) : 'Choisir un jour'}
              </strong>
            </span>
          )}
        />
        <div className={CHIP}>
          <span className={CHIP_KICK}>Horaire</span>
          {draft.day ? (
            <strong className="mt-0.5 flex min-w-0 items-center gap-1.5 text-sm font-bold">
              <DatePicker
                value={draft.day}
                onChange={() => { /* l’heure ne change pas le jour */ }}
                showTime
                timeOnly
                time={draft.startTime}
                onTimeChange={(time) => commit(setHearingStart(draftRef.current, draftRef.current.day, time))}
                disabled={disabled}
                aria-label="Heure de début"
                triggerClassName="w-auto"
                trigger={<span className="tabular-nums">{draft.startTime}</span>}
              />
              <span className="text-[#8a6d1f]">→</span>
              {draft.endTime ? (
                <>
                  <DatePicker
                    value={draft.day}
                    onChange={() => { /* le jour de fin reste celui du début */ }}
                    showTime
                    timeOnly
                    time={draft.endTime}
                    onTimeChange={(time) => commit(setHearingEnd(draftRef.current, time))}
                    disabled={disabled}
                    aria-label="Heure de fin"
                    triggerClassName="w-auto"
                    trigger={<span className="tabular-nums">{draft.endTime}</span>}
                  />
                  <button
                    type="button"
                    aria-label="Retirer l’heure de fin"
                    disabled={disabled}
                    onClick={() => commit(setHearingEnd(draftRef.current, ''))}
                    className="text-xs font-bold text-[#8a928c] hover:text-cime-text-primary"
                  >
                    ×
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  disabled={disabled || endLocked}
                  onClick={() => commit(setHearingEnd(draftRef.current, defaultEndTime(draftRef.current.startTime)))}
                  className="font-bold text-[#8a928c] disabled:opacity-50"
                >
                  —
                </button>
              )}
            </strong>
          ) : (
            <strong className={`${VALUE} text-[#8a928c]`}>—</strong>
          )}
        </div>
        <label className={CHIP}>
          <span className={CHIP_KICK}>Tribunal</span>
          <input
            value={draft.court}
            onChange={(event) => commit(setHearingCourt(draftRef.current, event.target.value))}
            placeholder="Tribunal judiciaire"
            disabled={disabled}
            aria-label="Tribunal"
            className="mt-0.5 w-full truncate border-0 bg-transparent p-0 text-sm font-bold text-cime-text-primary placeholder:text-[#8a928c] focus:outline-none disabled:opacity-50"
          />
        </label>
      </div>

      <div className="grid grid-cols-1 md:min-h-[320px] md:grid-cols-[1.25fr_0.85fr]">
        <section className="border-[#e6e2d8] px-[22px] pb-2 pt-[18px] md:border-r">
          <label className="block">
            <span className={LBL}>Descriptif</span>
            <Textarea
              value={draft.description}
              onChange={(event) => commit({ ...draftRef.current, description: event.target.value })}
              placeholder="Ce qui est appelé à l’audience"
              disabled={disabled}
              className={FIELD}
            />
          </label>
          <label className="mt-3 block">
            <span className={LBL}>Observations</span>
            <Textarea
              value={draft.notes}
              onChange={(event) => commit({ ...draftRef.current, notes: event.target.value })}
              disabled={disabled}
              className={`${FIELD} min-h-16`}
            />
          </label>
        </section>

        <aside className="px-[22px] pb-2 pt-[18px]">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-[11px] font-bold uppercase tracking-[0.12em] text-cime-text-muted">Personnes</span>
            <OwnerMenu
              ownerId={draft.ownerId}
              collaborators={collaborators}
              disabled={disabled}
              onPick={(ownerId) => commit({ ...draftRef.current, ownerId })}
            />
          </div>
          <PersonSearch
            title="Cabinet"
            placeholder="Rechercher dans le cabinet"
            empty="Aucun collaborateur."
            people={draft.people}
            disabled={disabled}
            options={collaborators.map((person) => ({
              person: { kind: 'collaborator' as const, id: person.id },
              name: person.name,
            }))}
            onToggle={(person) => commit(toggleHearingPerson(draftRef.current, person))}
          />
          <PersonSearch
            title="Parties du dossier"
            placeholder="Rechercher une partie"
            empty="Aucune partie sur ce dossier."
            people={draft.people}
            disabled={disabled}
            options={parties.map((person) => ({
              person: { kind: 'contact' as const, id: person.id },
              name: person.name,
              hint: person.role,
            }))}
            onToggle={(person) => commit(toggleHearingPerson(draftRef.current, person))}
          />
        </aside>
      </div>
    </div>
  );
}

function OwnerMenu({
  ownerId,
  collaborators,
  disabled,
  onPick,
}: {
  ownerId: string | null;
  collaborators: { id: string; name: string }[];
  disabled?: boolean;
  onPick: (ownerId: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const name = collaborators.find((person) => person.id === ownerId)?.name ?? 'Aucun';

  useEffect(() => {
    if (!open) return;
    function onDoc(event: MouseEvent) {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Responsable"
        onClick={() => setOpen((current) => !current)}
        className="max-w-[16rem] truncate rounded-full bg-cime-gold-bg px-2.5 py-1 text-xs font-bold text-cime-gold-text disabled:opacity-50"
      >
        Responsable · {name}
      </button>
      {open ? (
        <ul
          role="listbox"
          className="absolute right-0 z-20 mt-1 max-h-48 w-56 overflow-y-auto rounded-xl border border-[#e6e2d8] bg-white py-1 shadow-lg"
        >
          <li>
            <button
              type="button"
              role="option"
              aria-selected={ownerId === null}
              className="block w-full truncate px-3 py-1.5 text-left text-sm hover:bg-[#f6f4ee]"
              onClick={() => { onPick(null); setOpen(false); }}
            >
              Aucun
            </button>
          </li>
          {collaborators.map((person) => (
            <li key={person.id}>
              <button
                type="button"
                role="option"
                aria-selected={person.id === ownerId}
                className="block w-full truncate px-3 py-1.5 text-left text-sm hover:bg-[#f6f4ee]"
                onClick={() => { onPick(person.id); setOpen(false); }}
              >
                {person.name}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

type PersonOption = { person: HearingPerson; name: string; hint?: string };

function fold(value: string): string {
  return value.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
}

function byName(a: PersonOption, b: PersonOption): number {
  return a.name.localeCompare(b.name, 'fr', { sensitivity: 'base' });
}

function matchRank(option: PersonOption, needle: string): number {
  if (!needle) return 0;
  const name = fold(option.name);
  if (name.startsWith(needle)) return 0;
  if (name.split(/\s+/).some((part) => part.startsWith(needle))) return 1;
  if (name.includes(needle)) return 2;
  if (option.hint && fold(option.hint).includes(needle)) return 3;
  return 9;
}

function PersonSearch({
  title,
  placeholder,
  empty,
  people,
  options,
  disabled,
  onToggle,
}: {
  title: string;
  placeholder: string;
  empty: string;
  people: HearingPerson[];
  options: PersonOption[];
  disabled?: boolean;
  onToggle: (person: HearingPerson) => void;
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const sorted = useMemo(() => [...options].sort(byName), [options]);
  const needle = fold(query.trim());
  const matches = useMemo(() => {
    return sorted
      .map((option) => ({ option, rank: matchRank(option, needle) }))
      .filter((row) => row.rank < 9)
      .sort((a, b) => a.rank - b.rank || byName(a.option, b.option))
      .map((row) => row.option);
  }, [needle, sorted]);
  const chosen = sorted.filter((option) => personChecked(people, option.person));
  const found = matches[0] ?? null;
  const canSelect = !!found && !personChecked(people, found.person);

  function selectFound() {
    if (!found || !canSelect || disabled) return;
    onToggle(found.person);
    setQuery('');
  }

  return (
    <div className="mt-3">
      <span className={LBL}>{title}</span>
      <div className="relative">
        <div className="flex gap-1.5">
          <input
            value={query}
            disabled={disabled || options.length === 0}
            placeholder={options.length === 0 ? empty : placeholder}
            aria-label={placeholder}
            onChange={(event) => {
              setQuery(event.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onBlur={() => setOpen(false)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                selectFound();
              }
            }}
            className="h-9 min-w-0 flex-1 rounded-xl border border-[#e6e2d8] bg-white px-3 text-sm text-cime-text-primary placeholder:text-[#8a928c] focus:outline-none focus:ring-2 focus:ring-cime-primary/30 disabled:opacity-50"
          />
          <button
            type="button"
            disabled={disabled || !canSelect}
            onMouseDown={(event) => event.preventDefault()}
            onClick={selectFound}
            className="shrink-0 rounded-full bg-cime-primary px-3 text-[13px] font-bold text-white disabled:opacity-40"
          >
            Sélectionner
          </button>
        </div>
        {open && matches.length > 0 ? (
          <ul className="absolute left-0 right-0 top-[calc(100%+4px)] z-30 max-h-40 overflow-y-auto rounded-xl border border-[#e6e2d8] bg-white py-1 shadow-lg">
            {matches.map((option) => {
              const on = personChecked(people, option.person);
              const hit = !!needle && found?.person.id === option.person.id && found.person.kind === option.person.kind;
              return (
                <li key={`${option.person.kind}:${option.person.id}`}>
                  <button
                    type="button"
                    disabled={disabled}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => {
                      onToggle(option.person);
                      setQuery('');
                    }}
                    className={
                      hit
                        ? 'flex w-full items-baseline gap-2 bg-[#f6f4ee] px-3 py-1.5 text-left text-sm font-bold'
                        : 'flex w-full items-baseline gap-2 px-3 py-1.5 text-left text-sm hover:bg-[#f6f4ee]'
                    }
                  >
                    <span className="min-w-0 flex-1 truncate">{option.name}</span>
                    {option.hint ? <span className="shrink-0 text-[11px] text-[#8a928c]">{option.hint}</span> : null}
                    {on ? <span className="shrink-0 text-[11px] text-cime-primary">ajouté</span> : null}
                  </button>
                </li>
              );
            })}
          </ul>
        ) : null}
        {open && needle && matches.length === 0 ? (
          <p className="absolute left-0 right-0 top-[calc(100%+4px)] z-30 rounded-xl border border-[#e6e2d8] bg-white px-3 py-2 text-sm text-neutral-400 shadow-lg">
            Aucun résultat.
          </p>
        ) : null}
      </div>
      <div className="mt-1.5 h-[4.75rem] overflow-y-auto rounded-xl border border-[#e6e2d8] bg-white p-1.5">
        {chosen.length === 0 ? (
          <p className="px-1.5 py-1 text-sm text-[#8a928c]">Personne sélectionnée.</p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {chosen.map((option) => (
              <button
                key={`${option.person.kind}:${option.person.id}`}
                type="button"
                disabled={disabled}
                onClick={() => onToggle(option.person)}
                className="rounded-full border border-cime-primary bg-cime-primary px-2.5 py-1 text-[13px] text-white disabled:opacity-50"
              >
                {option.name}
                <span className="ml-1.5 text-[11px] text-white/70">×</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
